import React, { useState, useEffect } from 'react';
import { Card, Button, Alert, Spinner, Row, Col, Form, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { FaFile, FaFileImage, FaFilePdf, FaFileWord, FaFileExcel, FaFileArchive, FaFileCode, FaDownload, FaUser } from 'react-icons/fa';
import { submitReview, saveReviewDraft } from '../../services/reviewService';
import { getSubmissionFileUrl } from '../../services/submissionService';
import StarRating from './StarRating';
import { SimpleSection } from '../Section';
import ReviewEditor from '../editor/ReviewEditor';
import { formatDateTime } from '../../utils/dateUtils';
import './SubmissionReview.scss';
import config from '../../config';

const SubmissionReview = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [gradingCriteria, setGradingCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDraft, setIsDraft] = useState(true);
  
  const [formData, setFormData] = useState({
    criteriaScores: {},
    criteriaComments: {},
    generalComments: ''
  });
  const [lastSavedData, setLastSavedData] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const getFileIcon = (file) => {
    if (!file || !file.name) {
      return <FaFile className="file-icon" />;
    }
    
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (file.type && file.type.startsWith('image/')) return <FaFileImage className="file-icon" />;
    if (extension === 'pdf') return <FaFilePdf className="file-icon" />;
    if (['doc', 'docx'].includes(extension)) return <FaFileWord className="file-icon" />;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return <FaFileExcel className="file-icon" />;
    if (['zip', 'rar', 'tar', 'gz'].includes(extension)) return <FaFileArchive className="file-icon" />;
    if (['js', 'py', 'java', 'html', 'css', 'php', 'c', 'cpp', 'h', 'rb', 'json'].includes(extension)) {
      return <FaFileCode className="file-icon" />;
    }
    
    return <FaFile className="file-icon" />;
  };

  useEffect(() => {
    fetchSubmissionData();
    fetchGradingCriteria();
  }, [submissionId]);

  // Load existing draft after grading criteria are loaded
  useEffect(() => {
    if (gradingCriteria.length > 0) {
      loadExistingDraft();
    }
  }, [gradingCriteria]);

  // Auto-save every 30 seconds if form data has changed
  useEffect(() => {
    const interval = setInterval(() => {
      if (hasFormChanged() && !autoSaving && !submitting) {
        handleAutoSave();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [formData, lastSavedData, autoSaving, submitting]);

  const hasFormChanged = () => {
    if (!lastSavedData) return true;
    return JSON.stringify(formData) !== JSON.stringify(lastSavedData);
  };

  const handleAutoSave = async () => {
    // Only auto-save if there's meaningful content
    const hasContent = Object.values(formData.criteriaScores).some(score => score > 0) ||
                      Object.values(formData.criteriaComments).some(comment => comment.trim()) ||
                      formData.generalComments.trim();
    
    if (!hasContent) return;

    setAutoSaving(true);
    try {
      await saveReviewDraft(prepareReviewData());
      setLastSavedData({ ...formData });
      setLastSaveTime(new Date());
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setAutoSaving(false);
    }
  };

  const loadExistingDraft = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const judgeId = user.attributes.sub;
      
      const response = await fetch(`${config.api.url}reviews/submission/${submissionId}`);
      if (response.ok) {
        const data = await response.json();
        const existingReview = data.reviews?.find(r => r.judgeId === judgeId); // && r.isDraft);
        
        if (existingReview) {
          console.log('Loading existing draft:', existingReview);
          
          // Convert criteriaComments from array to object if needed
          let criteriaComments = existingReview.criteriaComments || {};
          if (Array.isArray(criteriaComments)) {
            const commentsObj = {};
            criteriaComments.forEach((comment, index) => {
              if (comment) {
                commentsObj[index] = comment;
              }
            });
            criteriaComments = commentsObj;
          }
          
          setFormData({
            criteriaScores: existingReview.criteriaScores || {},
            criteriaComments: criteriaComments,
            generalComments: existingReview.generalComments || ''
          });
          setLastSavedData({
            criteriaScores: existingReview.criteriaScores || {},
            criteriaComments: criteriaComments,
            generalComments: existingReview.generalComments || ''
          });
          setLastSaveTime(new Date(existingReview.lastModified));
          if (existingReview.isDraft===false) {
            setIsDraft(false);
          }
        }
      }
    } catch (err) {
      console.error('Error loading existing draft:', err);
    }
  };

  const prepareReviewData = () => {
    // Calculate weighted score
    let totalWeightedScore = 0;
    let totalWeight = 0;
    
    gradingCriteria.forEach((criteria, index) => {
      const score = formData.criteriaScores[index] || 0;
      const weight = criteria.weight;
      totalWeightedScore += (score * weight);
      totalWeight += weight;
    });
    
    const finalScore = totalWeight > 0 ? totalWeightedScore / totalWeight : 0;

    return {
      submissionId,
      criteriaScores: formData.criteriaScores,
      criteriaComments: formData.criteriaComments,
      generalComments: formData.generalComments,
      finalScore: Math.round(finalScore * 100) / 100,
      gradingCriteria: gradingCriteria
    };
  };

  const fetchGradingCriteria = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      
      const response = await fetch(`${config.api.url}admin/challenge-config?format=json`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.gradingCriteria && data.gradingCriteria.length > 0) {
          setGradingCriteria(data.gradingCriteria);
          
          // Initialize form data with criteria
          const initialScores = {};
          const initialComments = {};
          data.gradingCriteria.forEach((criteria, index) => {
            initialScores[index] = 0;
            initialComments[index] = '';
          });
          
          setFormData(prev => ({
            ...prev,
            criteriaScores: initialScores,
            criteriaComments: initialComments
          }));
        } else {
          // Fallback to default criteria
          setDefaultCriteria();
        }
      } else {
        setDefaultCriteria();
      }
    } catch (err) {
      console.error('Error fetching grading criteria:', err);
      setDefaultCriteria();
    }
  };

  const setDefaultCriteria = () => {
    const defaultCriteria = [
      { name: 'Technical Implementation', weight: 40, instructions: 'Evaluate the technical quality and implementation approach.' },
      { name: 'Innovation & Creativity', weight: 30, instructions: 'Assess the originality and creative problem-solving approach.' },
      { name: 'Documentation & Presentation', weight: 20, instructions: 'Review the quality of documentation and presentation.' },
      { name: 'Practical Impact', weight: 10, instructions: 'Consider the real-world applicability of the solution.' }
    ];
    
    setGradingCriteria(defaultCriteria);
    
    const initialScores = {};
    const initialComments = {};
    defaultCriteria.forEach((_, index) => {
      initialScores[index] = 0;
      initialComments[index] = '';
    });
    
    setFormData(prev => ({
      ...prev,
      criteriaScores: initialScores,
      criteriaComments: initialComments
    }));
  };

  const fetchSubmissionData = async () => {
    try {
      setLoading(true);
      
      // Fetch submission details and reviews from the correct endpoint
      const response = await fetch(`${config.api.url}reviews/submission/${submissionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch submission: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.submission) {
        setSubmission(null);
        setError('');
        return;
      }
      
      console.log('Submission data:', data.submission); // Debug log
      
      // Set submission data from the API response
      setSubmission({
        title: data.submission.title || 'Untitled',
        track: data.submission.track || 'General',
        timestamp: data.submission.timestamp,
        authors: data.submission.authors || [],
        files: data.submission.files || [],
        submissionId: data.submission.submissionId
      });
      
      setError('');
    } catch (err) {
      setError('Failed to load submission details. Please try again later.');
      console.error('Error fetching submission details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCriteriaScoreChange = (criteriaIndex, score) => {
    setFormData(prev => ({
      ...prev,
      criteriaScores: {
        ...prev.criteriaScores,
        [criteriaIndex]: score
      }
    }));
  };

  const handleCriteriaCommentChange = (criteriaIndex, comment) => {
    setFormData(prev => ({
      ...prev,
      criteriaComments: {
        ...prev.criteriaComments,
        [criteriaIndex]: comment
      }
    }));
  };

  const handleGeneralCommentsChange = (comments) => {
    setFormData(prev => ({
      ...prev,
      generalComments: comments
    }));
  };

  const validateForm = () => {
    // Check if all criteria have scores > 0
    const allScored = Object.values(formData.criteriaScores).every(score => score > 0);
    if (!allScored) {
      setError('Please provide ratings for all criteria (1-5 stars).');
      return false;
    }

    // Check if all criteria have comments (strip HTML tags for validation)
    const stripHtml = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };
    
    const allCommented = Object.values(formData.criteriaComments).every(comment => 
      stripHtml(comment || '').trim().length > 0
    );
    if (!allCommented) {
      setError('Please provide comments for all criteria.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await submitReview(prepareReviewData());
      
      setSuccess('Review submitted successfully!');
      
      // Refresh the reviews
      await fetchSubmissionData();
      
      // Reset form
      const resetScores = {};
      const resetComments = {};
      gradingCriteria.forEach((_, index) => {
        resetScores[index] = 0;
        resetComments[index] = '';
      });
      
      setFormData({
        criteriaScores: resetScores,
        criteriaComments: resetComments,
        generalComments: ''
      });
      setLastSavedData(null);

    } catch (err) {
      setError('Failed to submit review. Please try again.');
      console.error('Error submitting review:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await saveReviewDraft(prepareReviewData());
      setLastSavedData({ ...formData });
      setLastSaveTime(new Date());
      setSuccess('Review saved as draft successfully!');
      
      // Refresh the reviews to show the saved draft
      await fetchSubmissionData();

    } catch (err) {
      setError('Failed to save review draft. Please try again.');
      console.error('Error saving review draft:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      // Reviewers can't read other users' Amplify `private/` paths via IAM,
      // so we fetch a presigned URL from the backend instead of using
      // Storage.get directly. `file.name` is the original filename; the
      // backend matches on either the original or the sanitized name.
      const url = await getSubmissionFileUrl(submissionId, file.name);

      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      const isText = file.name.toLowerCase().match(/\.(txt|md|markdown)$/);

      if (isPdf || isText) {
        // Open in new tab
        window.open(url, '_blank');
      } else {
        // Download file
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error accessing file:', error);
      alert('Failed to access file. Please try again.');
    }
  };

  const handleDownloadAll = async () => {
    if (submission.files && submission.files.length > 1) {
      // Download each file individually with a small delay
      for (let i = 0; i < submission.files.length; i++) {
        setTimeout(() => {
          handleFileDownload(submission.files[i]);
        }, i * 500); // 500ms delay between downloads
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading submission details...</p>
      </div>
    );
  }

  if (!submission) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Submission Not Found</Alert.Heading>
        <p>The requested submission could not be found or is not assigned to you for review.</p>
        <Button variant="outline-danger" onClick={() => navigate('/review')}>
          Back to My Reviews
        </Button>
      </Alert>
    );
  }

  return (<>
    {/* Header Section */}
    <SimpleSection>
      <div className="d-flex justify-content-between align-items-center">
        <h1>Review Submission</h1>
        <Button variant="outline-secondary" onClick={() => navigate('/review')}>
          Back to My Reviews
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
    </SimpleSection>

    {/* Submission Details Section */}
    <SimpleSection>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Submission Details</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Title:</strong> {submission.title || 'Untitled'}</p>
              <p><strong>Track:</strong> {submission.track || 'General'}</p>
              <p><strong>Submitted:</strong> {formatDateTime(submission.timestamp)}</p>
            </Col>
            <Col md={6}>
              <p><strong>Authors:</strong></p>
              {submission.authors && submission.authors.length > 0 ? (
                submission.authors.map((author, index) => (
                  <div key={index} className="d-flex align-items-center mb-1">
                    <FaUser className="me-2 text-muted" size={14} />
                    <div>
                      {author.name}
                      {author.affiliation && <span className="text-muted">({author.affiliation})</span>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="d-flex align-items-center text-muted">
                  <FaUser className="me-2" size={14} />
                  <span>No authors listed</span>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </SimpleSection>

    {/* Files Section */}
    <SimpleSection>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Submission Files</h5>
            {submission.files && submission.files.length > 1 && (
              <Button variant="outline-primary" size="sm" onClick={handleDownloadAll}>
                Download All
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {submission.files && submission.files.length > 0 ? (
            <div className="files-grid">
              {submission.files.map((file, index) => (
                <div key={index} className="file-item d-flex align-items-center justify-content-between p-2 border rounded mb-2 bg-white">
                  <div className="file-info d-flex align-items-center">
                    {getFileIcon(file)}
                    <div className="file-details ms-2">
                      <div className="file-name fw-medium">{file.name}</div>
                      <div className="file-size text-muted small">{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => handleFileDownload(file)}
                    className="download-button"
                  >
                    <FaDownload className="me-1" /> 
                    {file.name.toLowerCase().endsWith('.pdf') || file.name.toLowerCase().match(/\.(txt|md|markdown)$/) 
                      ? 'Open' 
                      : 'Download'
                    }
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted">No files available for this submission.</p>
          )}
        </Card.Body>
      </Card>
    </SimpleSection>

    {/* Review Form Section */}
    <SimpleSection>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Submit Your Review</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {/* Dynamic Grading Criteria */}
            {gradingCriteria.map((criteria, index) => (
              <Card key={index} className="mb-4 border-light">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="mb-1">{criteria.name}</h6>
                      <Badge bg="info" className="me-2">{criteria.weight}% weight</Badge>
                      <small className="text-muted">{criteria.instructions}</small>
                    </div>
                  </div>

                  <Row>
                    <Col md={4}>
                      <Form.Label>Rating (1-5 stars)</Form.Label>
                      <div className="mb-2">
                        <StarRating
                          rating={formData.criteriaScores[index] || 0}
                          onRatingChange={(rating) => handleCriteriaScoreChange(index, rating)}
                          size="lg"
                        />
                      </div>
                      <small className="text-muted">
                        {formData.criteriaScores[index] === 0 && 'Please select a rating'}
                        {formData.criteriaScores[index] === 1 && 'Poor'}
                        {formData.criteriaScores[index] === 2 && 'Fair'}
                        {formData.criteriaScores[index] === 3 && 'Good'}
                        {formData.criteriaScores[index] === 4 && 'Very Good'}
                        {formData.criteriaScores[index] === 5 && 'Excellent'}
                      </small>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Comments for {criteria.name} *</Form.Label>
                        <ReviewEditor
                          value={formData.criteriaComments[index] || ''}
                          readOnly={!isDraft}
                          onChange={(content) => handleCriteriaCommentChange(index, content)}
                          placeholder={`Provide specific feedback on ${criteria.name.toLowerCase()}...`}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}

            {/* General Comments */}
            <Form.Group className="mb-4">
              <Form.Label>General Comments</Form.Label>
              <ReviewEditor
                value={formData.generalComments}
                readOnly={!isDraft}
                onChange={(content) => handleGeneralCommentsChange(content)}
                placeholder="Additional notes intended only for grading/evaluation (optional)."
              />
              <Form.Text className="text-muted">
                Additional notes intended only for grading/evaluation (optional).
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-between align-items-center">
              <div>
                {false && Object.values(formData.criteriaScores).some(score => score > 0) && (
                  <small className="text-muted">
                    Preview Score: {
                      Math.round(
                        (gradingCriteria.reduce((total, criteria, index) => {
                          return total + (formData.criteriaScores[index] * criteria.weight);
                        }, 0) / gradingCriteria.reduce((total, criteria) => total + criteria.weight, 0)) * 100
                      ) / 100
                    } / 5.0
                  </small>
                )}
                {(autoSaving || lastSaveTime) && (
                  <div className="mt-1">
                    <small className="text-muted">
                      {autoSaving ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-1" />
                          Auto-saving...
                        </>
                      ) : (
                        `Last saved: ${lastSaveTime.toLocaleTimeString()}`
                      )}
                    </small>
                  </div>
                )}
              </div>
              {isDraft && <div className="d-flex gap-2">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={saving || submitting}
                  onClick={handleSave}
                >
                  {saving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving Draft...
                    </>
                  ) : (
                    'Save Draft'
                  )}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting || saving}
                >
                  {submitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Submitting Review...
                    </>
                  ) : (
                    'Submit Review'
                  )}
                </Button>
              </div>}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </SimpleSection>
  </>);
};

export default SubmissionReview;
